import * as React from 'react'
import { clsx } from 'clsx'
import type * as Polymorphic from '@radix-ui/react-polymorphic'

import { Button, type ButtonProps } from '../Button'
import { useButtonGroupContext } from '../Button/Button'
import { VisuallyHidden } from '../VisuallyHidden'
import { Tooltip } from '../Tooltip'

import styles from './IconButton.module.css'

type IconButtonProps = ButtonProps & {
  /**
   * Icon displayed in the Button
   */
  children: React.ReactElement

  /**
   * Label for the Button
   */
  label: string

  /**
   * Tooltip to be displayed
   */
  tooltip?: React.ReactNode
}

type PolymorphicIconButton = Polymorphic.ForwardRefComponent<
  'button',
  IconButtonProps
>

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      children,
      label,
      tooltip,
      size,
      variant,
      className,
      disabled,
      ...props
    }: IconButtonProps,
    forwardedRef,
  ) => {
    // Use the same context that Button uses
    const groupContext = useButtonGroupContext()

    // Apply the same prioritization logic as in Button
    const finalSize =
      size !== undefined
        ? size
        : groupContext?.size !== undefined
        ? groupContext.size
        : 'medium'

    const finalVariant =
      variant !== undefined
        ? variant
        : groupContext?.variant !== undefined
        ? groupContext.variant
        : 'secondary'

    const finalDisabled =
      disabled !== undefined
        ? disabled
        : groupContext?.disabled !== undefined
        ? groupContext.disabled
        : false

    const iconButton = (
      <Button
        variant={finalVariant}
        size={finalSize}
        disabled={finalDisabled}
        className={clsx(styles.base, styles[finalSize], className)}
        ref={forwardedRef}
        {...props}
      >
        {children}
        <VisuallyHidden>{label}</VisuallyHidden>
      </Button>
    )

    if (tooltip) {
      return (
        <Tooltip content={tooltip} side="top" delay={800}>
          {iconButton}
        </Tooltip>
      )
    }

    return iconButton
  },
) as PolymorphicIconButton

IconButton.displayName = 'IconButton'

export { IconButton }
export type { IconButtonProps }
